"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { FaCircleXmark } from "react-icons/fa6";

import { usePlayerStore } from "@/store/playerStore";
import { useAuthStore } from "@/store/authStore";

import "aplayer/dist/APlayer.min.css";

const SUBSCRIBER_AUTOPLAY_LIMIT = 3;
const CONNECTIVITY_PROBE_URL = "/favicon.ico";
const CONNECTIVITY_PROBE_TIMEOUT_MS = 4000;
const PENDING_NAVIGATION_RETRY_MS = 3000;

export default function Player() {
  const router = useRouter();
  const pathname = usePathname();

  const playerContainerRef = useRef(null);
  const playerRef = useRef(null);

  const pathnameRef = useRef(pathname);
  const isSyncingPlayerRef = useRef(false);
  const syncedPlaylistRef = useRef(null);
  const lastProgressSecondRef = useRef(null);
  const lastBlockedTrackRef = useRef(null);

  const pendingNavigationRef = useRef(null);
  const isProbingConnectivityRef = useRef(false);
  const pendingNavigationRetryTimerRef = useRef(null);
  const offlineResumeTimeRef = useRef(null);
  const autoPlayedCountRef = useRef(0);
  const pauseOnNextSwitchRef = useRef(false);
  const lastSelectedTrackKeyRef = useRef(null);
  const isToggleMountRef = useRef(true);

  const [isPlayerReady, setIsPlayerReady] = useState(false);

  const currentTrack = usePlayerStore((state) => state.currentTrack);
  const playlist = usePlayerStore((state) => state.playlist);
  const toggleRequest = usePlayerStore((state) => state.toggleRequest);

  pathnameRef.current = pathname;

  const getTrackKey = useCallback((track) => {
    if (!track) return null;
    return track.id ?? track.url ?? track.slug ?? null;
  }, []);

  const safeClearList = useCallback((player) => {
    try {
      if (player?.list?.audios?.length) {
        player.list.clear();
      }
    } catch (error) {
      console.debug("APlayer list clear skipped:", error);
    }
  }, []);

  const isOffline = useCallback(() => {
    return typeof navigator !== "undefined" && navigator.onLine === false;
  }, []);

  const canNavigateNow = useCallback(() => {
    if (typeof document === "undefined") return false;
    if (document.visibilityState !== "visible") return false;
    if (isOffline()) return false;
    return true;
  }, [isOffline]);

  const clearPendingNavigationRetry = useCallback(() => {
    if (pendingNavigationRetryTimerRef.current) {
      clearTimeout(pendingNavigationRetryTimerRef.current);
      pendingNavigationRetryTimerRef.current = null;
    }
  }, []);

  // navigator.onLine بعد از آنلاک/اتصال مجدد قابل‌اعتماد نیست؛
  // قبل از ناوبری معوق، واقعاً بودن اتصال رو با یک HEAD سبک چک می‌کنیم
  // تا Next.js به hard navigation فال‌بک نکنه

  const verifyConnectivityAndNavigate = useCallback(async (targetPath) => {

    if (!targetPath || isProbingConnectivityRef.current) return;

    isProbingConnectivityRef.current = true;

    clearPendingNavigationRetry();

    try {

      const controller = new AbortController();

      const id = setTimeout(() => controller.abort(), CONNECTIVITY_PROBE_TIMEOUT_MS);

      await fetch(CONNECTIVITY_PROBE_URL, {

        method: "HEAD",

        cache: "no-store",

        signal: controller.signal,

      });

      clearTimeout(id);

      if (pathnameRef.current === targetPath) {

        pendingNavigationRef.current = null;

      } else {

        pendingNavigationRef.current = null;

        pathnameRef.current = targetPath;

        router.replace(targetPath, { scroll: false });

      }

    } catch (err) {

      console.warn("Connectivity probe failed, will retry navigation later.");

      // اگه فیل شد، ۳ ثانیه دیگه دوباره چک می‌کنیم (شاید DNS گوشی هنوز بالا نیومده)

      pendingNavigationRetryTimerRef.current = setTimeout(() => {

        verifyConnectivityAndNavigate(targetPath);

      }, PENDING_NAVIGATION_RETRY_MS);

    } finally {

      isProbingConnectivityRef.current = false;

    }

  }, [router, clearPendingNavigationRetry]);

  const navigateToTrack = useCallback(

    (track, options = { method: "replace" }) => {

      if (!track?.slug) return;

      const targetPath = `/story/${track.slug}`;

      if (pathnameRef.current === targetPath) {

        pendingNavigationRef.current = null;

        clearPendingNavigationRetry();

        return;

      }

      if (!canNavigateNow()) {

        pendingNavigationRef.current = targetPath;

        return;

      }

      pendingNavigationRef.current = null;

      pathnameRef.current = targetPath;

      if (options.method === "push") {

        router.push(targetPath);

      } else {

        router.replace(targetPath, { scroll: false });

      }

    },

    [router, canNavigateNow, clearPendingNavigationRetry],

  );

  const updateMediaSession = useCallback((track) => {

    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) {

      return;

    }

    if (!track || typeof window === "undefined" || !window.MediaMetadata) {

      return;

    }

    try {

      navigator.mediaSession.metadata = new window.MediaMetadata({

        title: track.name ?? track.title ?? "",

        artist: track.artist ?? "",

        album: track.album ?? "",

        artwork: track.cover ? [{ src: track.cover }] : [],

      });

    } catch (error) {

      console.error("Failed to update media session metadata:", error);

    }

  }, []);

  useEffect(() => {

    if (!playerContainerRef.current || playerRef.current) return;

    let isMounted = true;

    const initializePlayer = async () => {

      try {

        const { default: APlayer } = await import("aplayer");

        if (!isMounted || !playerContainerRef.current) return;

        const player = new APlayer({
          container: playerContainerRef.current,
          audio: [],
          theme: "#299c95",
          autoplay: false,
          listFolded: true,
          volume: 1,
          loop: "all",
          preload: "auto",
          order: "list",
          mutex: true,
        });

        player.on("listswitch", (event) => {
          if (isSyncingPlayerRef.current) return;

          const index = event?.index;
          if (typeof index !== "number") return;

          const state = usePlayerStore.getState();
          const tracks = Array.isArray(state.playlist) ? state.playlist : [];
          const playableTracks = tracks.filter((track) => track?.is_accessible !== false);
          const newTrack = playableTracks[index];
          if (!newTrack) return;

          // توقف autoplay بعد از SUBSCRIBER_AUTOPLAY_LIMIT
          if (pauseOnNextSwitchRef.current) {
            pauseOnNextSwitchRef.current = false;
            autoPlayedCountRef.current = 0;

            // مخصوصاً وقتی صفحه قفله باید pause با کمی تاخیر صدا زده بشه
            setTimeout(() => {
              player.pause();
            }, 100);

            lastProgressSecondRef.current = null;
            state.setWhatIsPlaying(newTrack);
            state.setLastSentTime(0);
            updateMediaSession(newTrack);
            playerRef.current.seek(0);
            navigateToTrack(newTrack);
            return;
          }

          lastProgressSecondRef.current = null;
          state.setWhatIsPlaying(newTrack);
          state.setLastSentTime(0);
          updateMediaSession(newTrack);
          navigateToTrack(newTrack);
        });


        player.on("play", () => {
          if (pauseOnNextSwitchRef.current) {
            pauseOnNextSwitchRef.current = false;
            autoPlayedCountRef.current = 0;
            player.pause();
            if (player.audio) player.audio.currentTime = 0;
            return;
          }
          usePlayerStore.getState().setIsPlaying(true);
          if (typeof navigator !== "undefined" && "mediaSession" in navigator) {
            navigator.mediaSession.playbackState = "playing";
          }
        });

        player.on("pause", () => {

          usePlayerStore.getState().setIsPlaying(false);

          if (typeof navigator !== "undefined" && "mediaSession" in navigator) {

            navigator.mediaSession.playbackState = "paused";

          }

        });

        player.on("timeupdate", () => {

          const currentSecond = Math.floor(

            Number(player.audio?.currentTime) || 0,

          );

          if (lastProgressSecondRef.current === currentSecond) {

            return;

          }

          lastProgressSecondRef.current = currentSecond;

          if (isOffline()) return;

          Promise.resolve(

            usePlayerStore.getState().sendPlayProgress(currentSecond),

          ).catch(() => { });

        });

        player.on("ended", () => {

          const playerState = usePlayerStore.getState();

          const authState = useAuthStore.getState();

          const hasActiveSubscription = Boolean(

            authState.user?.hasActiveSubscription,

          );

          if (!hasActiveSubscription) {

            player.pause();

            playerState.openSubscriptionModal();

            return;
          }

          autoPlayedCountRef.current += 1;

          if (autoPlayedCountRef.current >= SUBSCRIBER_AUTOPLAY_LIMIT) {

            pauseOnNextSwitchRef.current = true;

          }

        });

        player.on("error", () => {

          if (isOffline() && usePlayerStore.getState().isPlaying) {

            offlineResumeTimeRef.current =

              Number(player.audio?.currentTime) || 0;

            console.warn(

              "Audio playback failed while offline; will resume after reconnect.",

            );

            return;

          }

          console.error("APlayer failed to load or play the audio.");

        });

        if (typeof navigator !== "undefined" && "mediaSession" in navigator) {

          try {

            navigator.mediaSession.setActionHandler("play", () =>

              player.play(),

            );

            navigator.mediaSession.setActionHandler("pause", () =>

              player.pause(),

            );

            navigator.mediaSession.setActionHandler("previoustrack", () =>

              player.skipBack(),

            );

            navigator.mediaSession.setActionHandler("nexttrack", () =>

              player.skipForward(),

            );

          } catch (error) {

            console.error("Failed to set media session handlers:", error);

          }

        }

        playerRef.current = player;

        setIsPlayerReady(true);

      } catch (error) {

        console.error("Failed to initialize APlayer:", error);

      }

    };

    initializePlayer();

    return () => {

      isMounted = false;

      if (playerRef.current) {

        try {

          playerRef.current.destroy();

        } catch (error) {

          console.error("Failed to destroy APlayer:", error);

        }

        playerRef.current = null;

      }

      if (typeof navigator !== "undefined" && "mediaSession" in navigator) {

        try {

          navigator.mediaSession.setActionHandler("play", null);

          navigator.mediaSession.setActionHandler("pause", null);

          navigator.mediaSession.setActionHandler("previoustrack", null);

          navigator.mediaSession.setActionHandler("nexttrack", null);

          navigator.mediaSession.metadata = null;

        } catch (error) {

          console.debug("Media session cleanup skipped:", error);

        }

      }

      syncedPlaylistRef.current = null;

      isSyncingPlayerRef.current = false;

      lastProgressSecondRef.current = null;

      lastBlockedTrackRef.current = null;

      pendingNavigationRef.current = null;

      offlineResumeTimeRef.current = null;

      autoPlayedCountRef.current = 0;

      pauseOnNextSwitchRef.current = false;

      lastSelectedTrackKeyRef.current = null;

      clearPendingNavigationRetry();

    };

  }, [navigateToTrack, updateMediaSession, safeClearList, isOffline, clearPendingNavigationRetry]);

  useEffect(() => {

    const handleBackOnline = () => {

      const targetPath = pendingNavigationRef.current;

      if (targetPath && canNavigateNow()) {

        // تغییر مهم: مستقیماً router.replace صدا نمی‌زنیم چون ممکنه هنوز اینترنت فیک باشه.

        // اتصال واقعی رو اول چک می‌کنیم.

        verifyConnectivityAndNavigate(targetPath);

      }

      const resumeTime = offlineResumeTimeRef.current;

      const player = playerRef.current;

      if (player && resumeTime !== null && !isOffline()) {

        offlineResumeTimeRef.current = null;

        try {

          if (player.audio) {

            player.audio.currentTime = resumeTime;

          }

          player.play();

        } catch (error) {

          console.error("Failed to resume playback after reconnect:", error);

        }

      }

    };

    document.addEventListener("visibilitychange", handleBackOnline);

    window.addEventListener("online", handleBackOnline);

    window.addEventListener("focus", handleBackOnline);

    return () => {

      document.removeEventListener("visibilitychange", handleBackOnline);

      window.removeEventListener("online", handleBackOnline);

      window.removeEventListener("focus", handleBackOnline);

    };

  }, [router, canNavigateNow, isOffline, verifyConnectivityAndNavigate]);

  useEffect(() => {

    if (isToggleMountRef.current) {

      isToggleMountRef.current = false;

      return;

    }

    const player = playerRef.current;

    if (!player) return;

    player.toggle();

  }, [toggleRequest]);

  useEffect(() => {

    const player = playerRef.current;

    if (!isPlayerReady || !player) return;

    const tracks = Array.isArray(playlist) ? playlist : [];

    const playableTracks = tracks.filter(

      (track) => track?.is_accessible !== false,

    );

    if (tracks.length === 0) {

      isSyncingPlayerRef.current = true;

      try {

        player.pause();

        safeClearList(player);

      } finally {

        isSyncingPlayerRef.current = false;

      }

      syncedPlaylistRef.current = null;

      lastProgressSecondRef.current = null;

      lastBlockedTrackRef.current = null;

      pendingNavigationRef.current = null;

      offlineResumeTimeRef.current = null;

      autoPlayedCountRef.current = 0;

      pauseOnNextSwitchRef.current = false;

      lastSelectedTrackKeyRef.current = null;

      clearPendingNavigationRetry();

      return;

    }

    if (currentTrack?.is_accessible === false) {

      const blockedTrackKey = getTrackKey(currentTrack);

      isSyncingPlayerRef.current = true;

      try {

        player.pause();

        safeClearList(player);

      } finally {

        isSyncingPlayerRef.current = false;

      }

      syncedPlaylistRef.current = null;

      lastProgressSecondRef.current = null;

      offlineResumeTimeRef.current = null;

      autoPlayedCountRef.current = 0;

      pauseOnNextSwitchRef.current = false;

      if (lastBlockedTrackRef.current !== blockedTrackKey) {

        lastBlockedTrackRef.current = blockedTrackKey;

        usePlayerStore.getState().openSubscriptionModal();

      }

      return;

    }

    lastBlockedTrackRef.current = null;

    if (playableTracks.length === 0) {

      isSyncingPlayerRef.current = true;

      try {

        player.pause();

        safeClearList(player);

      } finally {

        isSyncingPlayerRef.current = false;

      }

      syncedPlaylistRef.current = null;

      offlineResumeTimeRef.current = null;

      autoPlayedCountRef.current = 0;

      pauseOnNextSwitchRef.current = false;

      return;

    }

    if (syncedPlaylistRef.current !== playlist) {

      isSyncingPlayerRef.current = true;

      try {

        player.pause();

        safeClearList(player);

        player.list.add(playableTracks);

        player.options.loop = playableTracks.length > 1 ? "all" : "none";

        player.audio.loop = false;

      } finally {

        isSyncingPlayerRef.current = false;

      }

      syncedPlaylistRef.current = playlist;

      lastProgressSecondRef.current = null;

    }

    if (!currentTrack) return;

    const currentTrackKey = getTrackKey(currentTrack);

    if (currentTrackKey === null) {

      console.error("The current track must contain id, url, or slug.");

      return;

    }

    const isNewSelection = lastSelectedTrackKeyRef.current !== currentTrackKey;

    if (isNewSelection) {

      lastSelectedTrackKeyRef.current = currentTrackKey;

      autoPlayedCountRef.current = 0;

      pauseOnNextSwitchRef.current = false;

      offlineResumeTimeRef.current = null;

    }

    const targetIndex = playableTracks.findIndex(

      (track) => String(getTrackKey(track)) === String(currentTrackKey),

    );

    if (targetIndex === -1) {

      console.error(

        "The current track was not found in the playable playlist.",

        currentTrack,

      );

      return;

    }

    let switched = false;

    if (player.list.index !== targetIndex) {

      isSyncingPlayerRef.current = true;

      try {

        player.list.switch(targetIndex);

        switched = true;

      } finally {

        isSyncingPlayerRef.current = false;

      }

      lastProgressSecondRef.current = null;

    }

    const targetTrack = playableTracks[targetIndex];

    const { whatIsPlaying, setWhatIsPlaying, setLastSentTime } =

      usePlayerStore.getState();

    if (

      String(getTrackKey(whatIsPlaying)) !== String(getTrackKey(targetTrack))

    ) {

      setWhatIsPlaying(targetTrack);

      setLastSentTime(0);

      lastProgressSecondRef.current = null;

    }

    updateMediaSession(targetTrack);

    navigateToTrack(currentTrack);

    if (isNewSelection || switched) {

      try {

        player.play();

      } catch (error) {

        console.error("Failed to start audio playback:", error);

      }

    }

  }, [

    isPlayerReady,

    playlist,

    currentTrack,

    getTrackKey,

    navigateToTrack,

    canNavigateNow,

    updateMediaSession,

    safeClearList,

    clearPendingNavigationRetry

  ]);

  const handleClosePlayer = () => {

    const player = playerRef.current;

    if (player) {

      player.pause();

    }

    pendingNavigationRef.current = null;

    offlineResumeTimeRef.current = null;

    clearPendingNavigationRetry();

    usePlayerStore.getState().stopPlaying();

  };

  const hasPlaylist = Array.isArray(playlist) && playlist.length > 0;

  return (
    <div
      className={[
        "border-b-2 border-c3 text-gray-300",
        "md:mx-auto md:max-w-xl",
        hasPlaylist ? "" : "hidden",
      ]
        .filter(Boolean)
        .join(" ")}
      aria-hidden={!hasPlaylist}
    >
      <div
        role="button"
        tabIndex={0}
        aria-label="بستن پلیر"
        title="بستن پلیر"
        className="text-xl text-red-600 bg-white w-max rounded-full"
        onClick={handleClosePlayer}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleClosePlayer();
          }
        }}
      >
        <FaCircleXmark />
      </div>

      <div
        ref={playerContainerRef}
        className="m-0 bg-c1"
        onClick={(e) => {
          const player = playerRef.current;
          if (!player) return;

          const target =
            e.target instanceof Element
              ? e.target.closest(".aplayer-pic, .aplayer-music")
              : null;

          if (!target) return;

          const currentAudio = player.list.audios[player.list.index];
          if (currentAudio) {
            navigateToTrack(currentAudio, { method: "push" });
          }
        }}
      />
    </div>
  );
}

